import { createApp } from "vue";
import { createPinia } from "pinia";
import router from "./router";
import { createHead } from "@vueuse/head";
import * as Sentry from "@sentry/vue";
import { createSentryPiniaPlugin } from "@sentry/vue";
import App from "./App.vue";
// bootstrap
import * as bootstrap from "bootstrap";
// modals
import "@/assets/sass/components/custom-modal.scss";

//Sweetalert
import VueSweetalert2 from "vue-sweetalert2";
import VueApexCharts from "vue3-apexcharts";

// perfect scrollbar
import PerfectScrollbar from "vue3-perfect-scrollbar";
import "vue3-perfect-scrollbar/dist/vue3-perfect-scrollbar.css";

//VueTelInput
import "vue-tel-input/dist/vue-tel-input.css";
import { i18n } from "@/i18n";

//Font Awesome and icon library
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { fas } from "@fortawesome/pro-solid-svg-icons";
import { fal } from "@fortawesome/pro-light-svg-icons";

//directives
import { tooltip } from "@/directives/tooltip";
import { access } from "@/directives/access";
import { hide } from "@/directives/hide";
import { safeHtml } from "@/directives/safe-html";

//store
import { useTrainingCourseStore } from "@/store/training-course/training-course";
import { useMainStore } from "@/store/main/main";
import { useTriadStore } from "@/store/triad/triad";
import { usePlatformStore } from "@/store/platform/platform";
import { useThemeStore } from "@/store/theme/theme";
import { useQuestionnaireStore } from "@/store/questionnaire/questionnaire";
import { useStudeaManagerQuestionnaireStore } from "@/store/studea_manager/studea-manager-questionnaire";
import { useUserStore } from "@/store/user/user";
import { useStudeaManagerStore } from "@/store/studea_manager/studea-manager";
import { useAdminStore } from "@/store/admin/admin";
import { useErrorStore } from "@/store/error/error";
import { useNotificationStore } from "@/store/notifications/notification";
// set default settings
import appSetting from "./app-setting";
import { Capacitor } from "@capacitor/core";
import { useChatbotStore } from "@/store/chatbot/chatbot";
import { useMessageStore } from "@/store/message/message";
import { useNewsStore } from "@/store/news/news";
import { useRegistrationStore } from "@/store/registration/registration";
import { useFormStore } from "@/store/form/form";
import { moduleAvailable } from "@/directives/module";
import { track } from "@/directives/track";
import { analyticsPlugin } from "@/plugins/analytics";
import { moduleConstantesPlugin } from "@/plugins/module-constantes";
if (!process.env.VUE_APP_SENTRY_ENV) {
  process.env.VUE_APP_SENTRY_ENV = "dev";
}
const jsonVersion = require("../version." + process.env.VUE_APP_SENTRY_ENV + ".json");

window.bootstrap = bootstrap;

//vue-meta
const head = createHead();

library.add(fas, fal);

const pinia = createPinia();
pinia.use(createSentryPiniaPlugin());

const app = createApp(App).use(pinia);
app
  .use(router)
  .use(head)
  .use(i18n)
  .use(VueSweetalert2)
  .use(VueApexCharts)
  .use(PerfectScrollbar)
  .use(analyticsPlugin)
  .use(moduleConstantesPlugin)
  .component("FontAwesomeIcon", FontAwesomeIcon);

app
  .directive("tooltip", tooltip)
  .directive("access", access)
  .directive("module", moduleAvailable)
  .directive("hide", hide)
  .directive("track", track)
  .directive("safe-html", safeHtml);

export const mainStore = useMainStore();
export const trainingCourseStore = useTrainingCourseStore();
export const questionnaireStore = useQuestionnaireStore();
export const triadStore = useTriadStore();
export const platformStore = usePlatformStore();
export const themeStore = useThemeStore();
export const studeaManagerQuestionnaireStore = useStudeaManagerQuestionnaireStore();
export const userStore = useUserStore();
export const studeaManagerStore = useStudeaManagerStore();
export const adminStore = useAdminStore();
export const errorStore = useErrorStore();
export const notificationStore = useNotificationStore();
export const chatbotStore = useChatbotStore();
export const messageStore = useMessageStore();
export const newsStore = useNewsStore();
export const registrationStore = useRegistrationStore();
export const formStore = useFormStore();

const REDACTED_URL_PARAMS = /((?:token|bearer|magic-link)=)[^&#\s]+/gi;
const redactSensitiveUrlParams = (value) =>
  typeof value === "string" ? value.replace(REDACTED_URL_PARAMS, "$1[redacted]") : value;

Sentry.init({
  app,
  environment: process.env.VUE_APP_SENTRY_ENV,
  dsn: "https://e5e3525696bb4e550c90671372c6d809@sentry.effetb.com/4",
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  attachProps: true,
  logErrors: true,
  release: process.env.VUE_APP_SENTRY_ENV + "-" + jsonVersion.version,
  enabled: process.env.VUE_APP_SENTRY_ENV === "prod" || process.env.VUE_APP_SENTRY_ENV === "uat",
  tracesSampleRate: 0.2, //  Capture 20% of the transactions
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  idleTimeout: 500, // Time in ms to wait before finishing a pageload/navigation span when no unfinished child spans remain.
  ignoreErrors: ["Non-Error promise rejection", "ResizeObserver loop limit exceeded", "Network request failed"],
  beforeSend(event) {
    if (event.request?.url) {
      event.request.url = redactSensitiveUrlParams(event.request.url);
    }
    if (event.transaction) {
      event.transaction = redactSensitiveUrlParams(event.transaction);
    }
    return event;
  },
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.data) {
      ["from", "to", "url"].forEach((key) => {
        if (breadcrumb.data[key]) {
          breadcrumb.data[key] = redactSensitiveUrlParams(breadcrumb.data[key]);
        }
      });
    }
    if (breadcrumb.message) {
      breadcrumb.message = redactSensitiveUrlParams(breadcrumb.message);
    }
    return breadcrumb;
  },
});

// Filet global : les rejets marqués `handled` ont déjà été notifiés au centre
// (fetchApi). On tait le warning console + la double capture Sentry ; le `catch`
// dans les vues devient ainsi optionnel (uniquement pour de la logique custom).
window.addEventListener(
  "unhandledrejection",
  (event) => {
    if (event.reason?.handled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true,
);

window.$appSetting = appSetting;
window.$appSetting.init();

// --- DEV ONLY : nettoyage des backdrops Bootstrap orphelins lors d'un Hot Module Replacement ---
// Les modales Bootstrap ajoutent un .modal-backdrop et la classe modal-open directement sur <body>,
// hors de l'arbre DOM géré par Vue. Lors d'un HMR, vue-loader remonte le composant (la modale perd
// sa classe "show" et disparaît) mais l'instance JS Bootstrap est détruite sans appeler dispose(),
// laissant le backdrop et le verrou de scroll orphelins sur le body. On les nettoie à chaque update.
if (module.hot) {
  const cleanupModalBackdrops = () => {
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");
  };
  module.hot.addStatusHandler((status) => {
    if (status === "apply" || status === "idle") {
      cleanupModalBackdrops();
    }
  });
}

if (router) {
  router.isReady().then(() => {
    app.mount("#app");
    useMainStore().setAppWebVersion(jsonVersion.version);
    Sentry.setTag("appClient", "web");
    Sentry.setTag("appVersion", jsonVersion.version);
    if (Capacitor.getPlatform() !== "web") {
      useMainStore().setAppBuildVersion(jsonVersion.build);
      Sentry.setTag("appClient", "mobile");
      Sentry.setTag("appBuild", jsonVersion.build);
    }
  });
}
